import { addCartItem } from './cart-request.js';

class ProductCard extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.revision = (this.revision || 0) + 1;
    this.selectedSwatch = this.querySelector('[data-swatch][aria-pressed="true"]');
    this.selectedLabel = this.querySelector('[data-swatch-label]')?.textContent;
    this.querySelectorAll('[data-swatch]').forEach((button) => { button.disabled = Boolean(this.adding); });
    this.addEventListener('click', this.selectSwatch, { signal: this.controller.signal });
    this.addEventListener('submit', this.addToCart, { signal: this.controller.signal });
    this.resetAction();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.revision += 1;
    this.selecting = false;
    this.showSwatch(this.selectedSwatch, this.selectedLabel);
    this.removeAttribute('aria-busy');
    const action = this.querySelector('[data-card-form] button');
    if (action && !this.adding) action.disabled = action.dataset.unavailable === 'true';
    clearTimeout(this.feedbackTimer);
    this.resetAction();
    const feedback = this.querySelector('[data-card-feedback]');
    if (feedback) feedback.textContent = '';
  }

  resetAction() {
    const form = this.querySelector('[data-card-form]');
    if (!form) return;
    const action = form.querySelector('button');
    if (!action) return;
    delete action.dataset.state;
    if (form.method === 'get' && action.dataset.unavailable !== 'true' && document.querySelector('quick-add-dialog') && window.HTMLDialogElement?.prototype.showModal) action.setAttribute('aria-haspopup', 'dialog');
    else action.removeAttribute('aria-haspopup');
    action.setAttribute('aria-label', action.dataset.unavailable === 'true'
      ? `${form.dataset.soldOutLabel}: ${action.dataset.productTitle}`
      : form.method === 'post' ? `${form.dataset.addLabel}: ${action.dataset.productTitle}` : `${form.dataset.optionsLabel}: ${action.dataset.productTitle}`);
  }

  setSelection(button) {
    const form = this.querySelector('[data-card-form]');
    if (!form) return;
    const variant = form.dataset.requiresOptions === 'true' ? null : button.dataset.variant;
    form.method = variant ? 'post' : 'get';
    form.action = variant ? form.dataset.addUrl : form.dataset.productUrl;
    form.elements.id.value = variant || '';
    form.elements.id.disabled = !variant;
    form.elements.quantity.disabled = !variant;
    form.elements.option_values.value = button.dataset.optionValue;
    form.elements.option_values.disabled = Boolean(variant);
    const action = form.querySelector('button');
    const unavailable = form.dataset.purchaseUnavailable === 'true' || button.hasAttribute('data-unavailable');
    action.dataset.unavailable = String(unavailable);
    action.disabled = unavailable;
    clearTimeout(this.feedbackTimer);
    this.resetAction();
    this.querySelector('[data-card-feedback]').textContent = '';
  }

  async addToCart(event) {
    const form = event.target;
    if (!form.matches('[data-card-form]')) return;
    if (form.method === 'get') {
      if (document.querySelector('dialog[open]')) return;
      const modal = document.querySelector('quick-add-dialog');
      if (!modal?.dataset.scriptUrl || !window.HTMLDialogElement?.prototype.showModal) return;
      event.preventDefault();
      const action = form.querySelector('button');
      if (this.opening || this.selecting || action.disabled) return;
      this.opening = true;
      action.setAttribute('aria-busy', 'true');
      try {
        // Shopify's versioned asset URL prevents reuse of an outdated modal lifecycle.
        const { openQuickAdd } = await import(modal.dataset.scriptUrl);
        if (this.isConnected) await openQuickAdd(form, action);
      } catch {
        if (this.isConnected && !document.querySelector('dialog[open]')) {
          const url = new URL(form.dataset.productUrl, location.origin);
          for (const [key, value] of new FormData(form)) url.searchParams.set(key, value);
          location.assign(url.href);
        }
      } finally {
        this.opening = false;
        action.removeAttribute('aria-busy');
      }
      return;
    }
    if (form.method !== 'post') return;
    if (!document.querySelector('cart-panel[data-cart-ready]')) return;
    event.preventDefault();
    const action = form.querySelector('button');
    if (this.adding || this.selecting || action.disabled) return;
    this.adding = true;
    const feedback = this.querySelector('[data-card-feedback]');
    clearTimeout(this.feedbackTimer);
    this.resetAction();
    feedback.replaceChildren();
    const request = addCartItem(form);
    action.disabled = true;
    action.setAttribute('aria-busy', 'true');
    this.querySelectorAll('[data-swatch]').forEach((button) => { button.disabled = true; });
    try {
      const result = await request;
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { result, item: result.items?.[0] || result } }));
      if (!this.isConnected) return;
      action.dataset.state = 'added';
      action.setAttribute('aria-label', `${form.dataset.success}: ${action.dataset.productTitle}`);
      this.feedbackTimer = setTimeout(() => this.resetAction(), 1800);
    } catch (error) {
      if (this.isConnected) {
        feedback.textContent = [error.message, error.uncertain ? form.dataset.uncertain : form.dataset.error].filter(Boolean).join(' ');
        if (error.uncertain) {
          const link = document.createElement('a');
          link.href = form.dataset.cartUrl;
          link.dataset.cartOpen = '';
          link.textContent = form.dataset.cartLabel;
          feedback.append(' ', link);
        }
      }
    } finally {
      this.adding = false;
      action.disabled = action.dataset.unavailable === 'true';
      action.removeAttribute('aria-busy');
      this.querySelectorAll('[data-swatch]').forEach((button) => { button.disabled = false; });
    }
  }
  async selectSwatch(event) {
    const button = event.target.closest('[data-swatch]');
    if (!button || !this.contains(button) || this.adding) return;
    const template = this.querySelector(`[data-swatch-preview="${button.dataset.swatch}"]`);
    if (!template) return;
    const revision = ++this.revision;
    const fragment = this.ownerDocument.importNode(template.content, true);
    const media = fragment.querySelector('.product-card__image');
    const image = media?.querySelector('img');
    const error = this.querySelector('[data-swatch-error]');
    error.textContent = '';
    this.showSwatch(button, button.getAttribute('aria-label'));
    this.selecting = true;
    const action = this.querySelector('[data-card-form] button');
    if (action) action.disabled = true;
    this.setAttribute('aria-busy', 'true');
    try {
      // Decode only the requested image; retain the previous view on failure.
      if (image) await image.decode();
      if (!this.isConnected || revision !== this.revision) return;
      if (media) this.querySelector('.product-card__media').replaceChildren(media);
      const price = fragment.querySelector('[data-preview-price]');
      if (price) this.querySelector('[data-card-price]').replaceChildren(...price.childNodes);
      this.querySelectorAll('[data-card-link]').forEach((link) => { link.href = button.dataset.url; });
      this.selectedSwatch = button;
      this.selectedLabel = button.getAttribute('aria-label');
      this.setSelection(button);
    } catch {
      if (this.isConnected && revision === this.revision) {
        this.showSwatch(this.selectedSwatch, this.selectedLabel);
        error.textContent = error.dataset.message;
      }
    } finally {
      if (revision === this.revision) {
        this.selecting = false;
        this.removeAttribute('aria-busy');
        if (action) action.disabled = action.dataset.unavailable === 'true';
      }
    }
  }

  showSwatch(button, name) {
    this.querySelectorAll('[data-swatch]').forEach((swatch) => {
      swatch.setAttribute('aria-pressed', String(swatch === button));
    });
    const label = this.querySelector('[data-swatch-label]');
    if (label) label.textContent = name;
  }
}

if (!customElements.get('product-card')) customElements.define('product-card', ProductCard);
